import api from '@/api'
import vuex from '@/store'

export default {
  state: {
    assets: [],
    currentAsset: {},
    lastUpdatedAt: 0,
    activeUploads: [],
    activeUploadDefaults: {
      name: '',
      file: null,
      isStarted: false,
      isUploading: false,
      progress: null,
      cancelTokenSource: null,
      newAsset: null,
      archive: false,
      station: null,
      destinations: []
    }
  },
  mutations: {
    LOAD_ASSETS(state, assets) {
      state.assets = assets
      console.debug(`LOAD_ASSETS`, assets)
    },
    LOAD_ASSET(state, asset) {
      const index = state.assets.findIndex((s) => s.id == asset.id)
      if (index > -1) state.assets.splice(index, 1, asset)
      else state.assets.push(asset)
      console.debug(`LOAD_ASSET`, asset)
    },
    SET_ASSET(state, object) {
      state.currentAsset = object
      console.debug(`SET_ASSET`, object?.name, object?.story_id)
    },
    SET_MEDIA_LAST_UPDATED_AT(state, updated_at_ts) {
      state.lastUpdatedAt = updated_at_ts
    },
    ADD_ACTIVE_UPLOAD(state, upload) {
      if (!upload?.file?.name) throw new Error(`No upload to add`)
      let uIndex = state.activeUploads.findIndex((u) => u.file.name == upload.file.name)
      if (uIndex == -1) {
        state.activeUploads.push(upload)
        return state.activeUploads.slice(-1)[0]
      } else throw new Error(`Already uploading '${upload.file.name}`)
    },
    REMOVE_ACTIVE_UPLOAD(state, uploadName) {
      let uIndex = state.activeUploads.findIndex((u) => u.name == uploadName)
      if (uIndex == -1) return
      state.activeUploads.splice(uIndex, 1)
    }
  },
  actions: {
    async getMediaChanges({ commit, state, dispatch }) {
      let response = await api.newsdesk.getChanges()
      if (!response) return
      // find story change
      let mediaAssetsChange = response.data?.find((c) => c.change_type == 'media')

      if (state.lastUpdatedAt < mediaAssetsChange?.updated_at_ts) {
        commit('SET_MEDIA_LAST_UPDATED_AT', mediaAssetsChange?.updated_at_ts)
        await dispatch('loadAssets', true)
      }
      return
    },
    async loadAssets({ commit, state, dispatch }, skipCache) {
      // get list of stories from API
      const _s = await api.newsdesk.getMediaAssets(null, skipCache)
      const assets = _s?.data?.results ?? []

      commit('LOAD_ASSETS', assets)

      // We should update the current story object also.
      if (state?.currentAsset?.id) {
        let match = assets.find((asset) => asset.id == state.currentAsset.id)

        if (match) {
          dispatch('setAsset', match)
        }
      }
    },

    async loadAsset({ commit, state, dispatch }, { skipCache, id }) {
      const _s = await api.newsdesk.getMediaAsset(id, skipCache)
      const asset = _s.data // ?.data?.find(s => s.id == id)
      commit('LOAD_ASSET', asset)
      dispatch('setAsset', asset)
      return asset
    },

    async setAsset({ commit, state }, asset) {
      commit('SET_ASSET', asset)
    },
    async saveAsset({ commit, state }, { id, data }) {
      console.debug('SAVING ASSET', id, data)
      const _s = await api.newsdesk.updateMediaAsset(id, data)
      let updatedAsset = _s?.data
      if (updatedAsset) commit('LOAD_ASSET', updatedAsset)
      return updatedAsset
    },
    pushActiveUpload({ commit, state, dispatch }, upload) {
      let activeUpload = { ...state.activeUploadDefaults, ...upload }
      activeUpload.cancelTokenSource = api.newsdesk.getCancelTokenSource()

      commit('ADD_ACTIVE_UPLOAD', activeUpload)
      return activeUpload
    },
    async startActiveUpload({ commit, state, dispatch }, uploadName) {
      // find the active upload index
      let uIndex = state.activeUploads.findIndex((u) => u.name == uploadName)
      if (uIndex == -1) return
      // get the upload object
      let upload = state.activeUploads[uIndex]
      let data = {
        name: upload.name,
        file: upload.file,
        story_id: upload.storyId,
        archive: upload.archive ? 1 : 0,
        station: upload.station ?? vuex.getters.currentStation.station,
        destinations: upload.destinations,
        tags: upload.tags
      }

      upload.isUploading = true
      try {
        await api.newsdesk.addMediaAsset(
          data,
          (progress) => {
            // update the upload progress
            if (upload.file) upload.file.active = true
            upload.isStarted = true
            upload.progress = progress
          },
          upload.cancelTokenSource.token,
          (newAsset) => {
            upload.newAsset = newAsset
          }
        )
        upload.file.success = true

        setTimeout(() => {
          try {
            dispatch('removeActiveUpload', uploadName)
          } catch (error) {
            console.error('Failed to remove active upload', error)
          }
        }, 1000)
      } catch (error) {
        upload.file.success = false
        // only set error if it has a message. Cancelled uploads will not have a message
        if (error.message) {
          upload.error = error
          console.error('Failed to upload', upload)
          console.error(error)
        } else console.warn('Upload cancelled', upload)
      } finally {
        upload.isUploading = false
        await dispatch('getMediaChanges')
      }
      return upload
    },

    // tell the server to abort the multi part upload so the server can clean up
    abortActiveUpload({ commit, state, dispatch }, uploadName) {
      let uIndex = state.activeUploads.findIndex((u) => u.name == uploadName)
      if (uIndex == -1) return
      let upload = state.activeUploads[uIndex]
      if (!upload.newAsset?.id) return
      return api.newsdesk.abortMultiPartUpload(upload.newAsset.id, upload.file.name)
    },

    async cancelActiveUpload({ commit, state, dispatch }, uploadName) {
      let uIndex = state.activeUploads.findIndex((u) => u.name == uploadName)
      if (uIndex == -1) return
      state.activeUploads[uIndex]?.cancelTokenSource?.cancel()
      // abort the multi part upload so the server can clean up
      try {
        await dispatch('abortActiveUpload', uploadName)
      } catch (error) {
        console.error('Failed to abort multi part upload', error)
      }
      return dispatch('removeActiveUpload', uploadName)
    },
    removeActiveUpload({ commit, state, dispatch }, uploadName) {
      return commit('REMOVE_ACTIVE_UPLOAD', uploadName)
    }
  },
  getters: {
    assets(state) {
      return state.assets
    },
    currentAsset(state) {
      return state.currentAsset ?? {}
    },
    activeUploads(state) {
      return state.activeUploads
    }
  }
}
